import type { App } from 'vue'

import { setupLayouts } from 'virtual:generated-layouts'
import {
  createRouter as createClientRouter,
  createWebHistory,
  setupDataFetchingGuard,
} from 'vue-router/auto'

/**
 * routes are generated using vite-plugin-pages
 * each .vue files located in the ./src/pages are registered as a route
 * @see https://github.com/hannoeru/vite-plugin-pages
 */

import { setupRouterGuard } from '/@/router/guard'
import { flatten } from '/@/utils'

/**
 * By default, these plugins checks the folder at src/pages for any .vue files
 * and generates the corresponding routing structure basing itself in the file name.
 * This way, you no longer need to maintain a routes array when adding routes to your application,
 * instead just add the new .vue component to the routes folder and let this plugin do the rest!
 *
 * Let's take a look at a simple example:
 *
 * src/pages/
 * ├── placeload.vue
 * ├── about.vue
 * └── users/
 *     ├── placeload.vue
 *     └── [id]@details.vue
 * This will generate the following routes:
 *
 * /: -> renders the placeload.vue component
 * /about: -> renders the about.vue component
 * /users: -> renders the users/placeload.vue component
 * /users/:id: -> renders the users/[id]@details.vue component. id becomes a route param.
 *
 * View more examples:
 * @see https://github.com/posva/unplugin-vue-router#routes-folder-structure
 *
 * You can redirect to route with <route> tag
 * <route lang="yaml">
 *   meta:
 *      layout: auth-layout
 * </route>
 */

export function createRouter() {
  const router = createClientRouter({
    /**
     * If you need to serve vuero under a subdirectory,
     * you have to set the name of the directory in createWebHistory here
     * and update "base" config in vite.config.d.ts
     */
    // history: createWebHistory('my-subdirectory'),
    // We use subdirectory for feature branch deployment.
    history: createWebHistory(import.meta.env.BASE_URL),
    /**
     * You can extend existing routes:
     */
    extendRoutes: (routes) => {
      // See: https://github.com/posva/unplugin-vue-router/discussions/63
      flatten(routes).forEach((route) => {
        if (route.meta?.redirect) {
          route.redirect = { name: route.meta.redirect }
        }
        // Unplugin-vue-router now working with multiple directories
        // It's work around.
        if (route.path === '/docs') {
          route.meta = { ...route.meta, layout: 'docs-layout' }
        }
      })
      return setupLayouts(routes)
    },
    /*
     * Handle scroll behavior between routes
     */
    scrollBehavior: (to, from, savedPosition) => {
      // Scroll to heading on click
      if (to.hash) {
        // When click in scrollspy component's menu item, return
        if (from && to.name === from.name) {
          return
        }

        if (to.hash === '#') {
          return {
            top: 0,
            behavior: 'smooth',
          }
        }

        const el = document.querySelector(to.hash)

        // vue-router does not incorporate scroll-margin-top on its own.
        if (el) {
          const top = parseFloat(getComputedStyle(el).scrollMarginTop)
          if (el instanceof HTMLElement) {
            el.focus()
          }

          return {
            el: to.hash,
            behavior: 'smooth',
            top,
          }
        }

        return {
          el: to.hash,
          behavior: 'smooth',
        }
      }

      // Scroll to top of window
      if (savedPosition) {
        return savedPosition
      }

      // If the page has a "noScroll" tag, it will not scroll to the top when the url is updated.
      if (to.meta?.noScroll) {
        return false
      }

      if (to.path !== from.path) {
        return { top: 0 }
      }
      return { top: 0 }
    },
  })

  /**
   * Data Fetching is an experimental feature from vue & vue-router
   *
   * @see https://github.com/vuejs/rfcs/discussions/460
   * @see https://github.com/posva/unplugin-vue-router/tree/main/src/data-fetching
   */
  setupDataFetchingGuard(router)

  return router
}

export const router = createRouter()

// config router
export function setupRouter(app: App<Element>) {
  app.use(router)

  // setup all guard
  setupRouterGuard(router)
}
